import React, { useState } from 'react';
import { Button, Box, Tooltip } from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

interface PatchType {
  sid: string;
}

interface DownloadConfigFileProps {
  configText: string;
  patches: PatchType[];
  placeholders: { sid: string };
  prefix: 'patch' | 'upg';
  onCopy?: () => void;
  disabled?: boolean;
}

const getConfigFilename = (patches: PatchType[], placeholders: { sid: string }, prefix: 'patch' | 'upg') => {
  if (patches.length === 1) {
    const sid = patches[0].sid || placeholders.sid || 'DB1';
    return `${prefix}_${sid}.cfg`;
  } else {
    // Only use first two placeholders
    const sids = patches.slice(0, 2).map((p, i) => p.sid || `DB${i + 1}`);
    return `${prefix}_${sids.join('_')}.cfg`;
  }
};

const DownloadConfigFile: React.FC<DownloadConfigFileProps> = ({ configText, patches, placeholders, prefix, onCopy, disabled = false }) => {
  const filename = getConfigFilename(patches, placeholders, prefix);
  const [copied, setCopied] = useState(false);

  const handleDownload = () => {
    const blob = new Blob([configText], { type: 'text/plain' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(configText);
    setCopied(true);
    if (onCopy) onCopy();
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      <Button 
        variant="contained" 
        color="primary" 
        startIcon={<DownloadOutlinedIcon />}
        sx={{
          fontWeight: 'bold',
          borderRadius: 8,
          px: 4,
          py: 1.5,
          fontSize: 16,
          boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
          '&:hover': { background: 'primary.dark' },
        }}
        onClick={handleDownload}
        disabled={disabled}
      >
        Download Config File
      </Button>
      <Tooltip 
        title={copied ? "Copied!" : "Copy to clipboard"}
        arrow
        placement="top"
      >
        <Button
          variant="contained"
          color="primary"
          startIcon={<ContentCopyIcon />}
          onClick={handleCopy}
          sx={{
            fontWeight: 'bold',
            borderRadius: 8,
            px: 4,
            py: 1.5,
            fontSize: 16,
            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.10)',
            '&:hover': { background: 'primary.dark' },
          }}
          disabled={disabled}
        >
          Copy to Clipboard
        </Button>
      </Tooltip>
    </Box>
  );
};

export default DownloadConfigFile; 